import { MAP_EVENTS, useMap } from 'es-map-widget';
// eslint-disable-next-line import/no-extraneous-dependencies
import TileLayer from 'ol/layer/Tile';
// eslint-disable-next-line import/no-extraneous-dependencies
import XYZ from 'ol/source/XYZ';
import { useCallback, useEffect, useRef } from 'react';
import { LOGGED_IN_USER_KEY } from '../../config';
import { MAP_LAYER_NAMES } from '../../constants';

const PLANT_OCCURRENCE_LAYER_PREFIX = 'PLANT_OCCURRENCE_TILE';
const PLANT_OCCURRENCE_Z_INDEX = 9;
const PLANT_OCCURRENCE_ZOOM_LIMITS = {
    SOURCE_MIN_ZOOM: 3,
    COARSE_MIN_ZOOM: 4,
    COARSE_MAX_ZOOM: 7,
    DETAILED_MIN_ZOOM: 8,
    DETAILED_MAX_SOURCE_ZOOM: 10,
};
const PLANT_OCCURRENCE_RESOLUTION_LAYERS = [
    {
        resolution: '500m',
        minZoom: PLANT_OCCURRENCE_ZOOM_LIMITS.COARSE_MIN_ZOOM - 1,
        maxZoom: PLANT_OCCURRENCE_ZOOM_LIMITS.COARSE_MAX_ZOOM,
        sourceMinZoom: PLANT_OCCURRENCE_ZOOM_LIMITS.SOURCE_MIN_ZOOM,
        sourceMaxZoom: PLANT_OCCURRENCE_ZOOM_LIMITS.COARSE_MAX_ZOOM,
    },
    {
        resolution: '100m',
        minZoom: PLANT_OCCURRENCE_ZOOM_LIMITS.DETAILED_MIN_ZOOM - 1,
        sourceMinZoom: PLANT_OCCURRENCE_ZOOM_LIMITS.SOURCE_MIN_ZOOM,
        sourceMaxZoom: PLANT_OCCURRENCE_ZOOM_LIMITS.DETAILED_MAX_SOURCE_ZOOM,
    },
];

const getUniquePlantCodes = (plantCodes = []) =>
    Array.from(new Set(plantCodes.filter(Boolean).map((plantCode) => plantCode.trim())));

const PlantOccurrenceDataprovider = ({ layerData, setSource }) => {
    const map = useMap();
    const tileLayersRef = useRef([]);

    const clearTileLayers = useCallback(() => {
        const internalMap = map?.map;

        if (internalMap) {
            tileLayersRef.current.forEach((layer) => {
                internalMap.removeLayer(layer);
            });
        }

        tileLayersRef.current = [];
    }, [map]);

    const setTileLayersVisibility = useCallback((isVisible) => {
        tileLayersRef.current.forEach((layer) => {
            layer.setVisible(isVisible);
        });
    }, []);

    const addTileLayers = useCallback(() => {
        const internalMap = map?.map;
        const user = JSON.parse(localStorage.getItem(LOGGED_IN_USER_KEY));
        const plantCodes = getUniquePlantCodes(layerData.plantCodes);

        if (!internalMap || !layerData.isLayerVisible || plantCodes.length === 0 || !user?.token) {
            clearTileLayers();
            return;
        }

        clearTileLayers();

        tileLayersRef.current = plantCodes.flatMap((plantCode) =>
            PLANT_OCCURRENCE_RESOLUTION_LAYERS.map(
                ({ resolution, minZoom, maxZoom, sourceMinZoom, sourceMaxZoom }) => {
                    const layer = new TileLayer({
                        projection: 'EPSG:23700',
                        source: new XYZ({
                            url: `/api/layers/plant-mbtiles/${layerData.year}/${encodeURIComponent(
                                plantCode
                            )}/${resolution}/{z}/{x}/{y}/image.png?token=${user.token}`,
                            minZoom: sourceMinZoom,
                            maxZoom: sourceMaxZoom,
                            transition: 0,
                            interpolate: false,
                        }),
                        minZoom,
                        maxZoom,
                        zIndex: PLANT_OCCURRENCE_Z_INDEX,
                        properties: {
                            name: `${PLANT_OCCURRENCE_LAYER_PREFIX}:${resolution}:${plantCode}`,
                        },
                    });

                    internalMap.addLayer(layer);
                    return layer;
                }
            )
        );
    }, [clearTileLayers, layerData, map]);

    useEffect(() => {
        setSource(null);
    }, [setSource]);

    useEffect(() => {
        addTileLayers();

        return () => {
            clearTileLayers();
        };
    }, [addTileLayers, clearTileLayers]);

    useEffect(() => {
        const visibilityListener = map.addEventListener(MAP_EVENTS.visibilityChange, () => {
            setTileLayersVisibility(map.getLayerVisibility(MAP_LAYER_NAMES.PLANT_OCCURRENCE));
        });

        return () => {
            map.removeEventListener(MAP_EVENTS.visibilityChange, visibilityListener);
        };
    }, [map, setTileLayersVisibility]);

    return null;
};

export default PlantOccurrenceDataprovider;
